/**
 * Canonical URLs for pages that live outside this app: Reflex properties
 * hosted elsewhere (GitHub, the Runloop marketing site, the Statuspage-backed
 * status site), the legal policies a user accepts at sign-in, and the
 * third-party consoles we send admins to.
 *
 * None of them can be expressed as `ROUTES` entries or appear in our sitemap.
 * Centralising them here keeps every surface — marketing footers, auth pages,
 * docs, SSR structured data, secret dialogs — pointing at the same URL when
 * one moves.
 */

/**
 * Opens the console's Generate API Key modal on arrival.
 *
 * Host-less on purpose: the deployment supplies it, since Reflex also talks to
 * `platform.runloop.pro` and per-VPC hosts. Join with
 * {@link runloopCreateApiKeyUrl}.
 *
 * The console matches `showAPIKeyModal` case-sensitively — only the literal
 * `true` works, never `1`/`yes`/`TRUE`.
 */
export const RUNLOOP_CREATE_API_KEY_PATH = '/settings?showAPIKeyModal=true';

/** Production Runloop console, used when a deployment's own URL isn't known. */
export const RUNLOOP_PLATFORM_URL = 'https://platform.runloop.ai';

/**
 * Key-creation deeplink on `platformUrl`'s console.
 *
 * Pass `platformUrl` from `GET /api/config` (web) or
 * `deriveRunloopPublicDomains(RUNLOOP_BASE_URL)` (server). Falls back to
 * production when unknown — config still loading, or no deployment context.
 */
export function runloopCreateApiKeyUrl(platformUrl?: string | null): string {
  const base = platformUrl?.trim() ? platformUrl.trim() : RUNLOOP_PLATFORM_URL;
  // Scanned, not `/\/+$/` — that regex on a deployment-supplied string is a
  // polynomial-ReDoS vector (CodeQL `js/polynomial-redos`).
  let end = base.length;
  while (end > 0 && base[end - 1] === '/') end -= 1;
  return `${base.slice(0, end)}${RUNLOOP_CREATE_API_KEY_PATH}`;
}

export const EXTERNAL_LINKS = {
  /** Runloop's corporate site. */
  runloop: 'https://runloop.ai',
  /** Public uptime and incident history for Reflex and Runloop. */
  status: 'https://status.runloop.ai',
  /** The Reflex source repository. */
  github: 'https://github.com/runloopai/reflex-os',
  /** What you may and may not do with Reflex and Runloop. */
  acceptableUsePolicy: 'https://runloop.ai/legal/aup',
  /** The contract a user accepts by signing in. */
  termsOfService: 'https://runloop.ai/legal/terms-of-service',
  /** How we collect, store, and share user data. */
  privacyPolicy: 'https://runloop.ai/legal/privacy-policy',
  /**
   * Key creation on the **production** console — the fallback for surfaces
   * with no deployment context, including the onboarding docs page (its link
   * lives in a markdown string; `docs-content-links.test.ts` pins that copy).
   *
   * Anything that can see the deployment's platform URL should call
   * {@link runloopCreateApiKeyUrl} instead.
   */
  runloopCreateApiKey: runloopCreateApiKeyUrl(),
} as const;

/**
 * The legal policies every pre-sign-in surface must link to, in the order
 * they should be rendered.
 *
 * Sign-in is the moment a user accepts these, so the marketing home page and
 * the auth pages both have to show the full set. Rendering them from one list
 * means adding or renaming a policy is a single edit here rather than a hunt
 * through footers — and it is why the labels live alongside the URLs.
 */
export const LEGAL_LINKS = [
  {
    key: 'terms',
    /** Standalone form, for footer columns and link rows. */
    label: 'Terms of Service',
    /** Mid-sentence form, for the sign-in consent line. */
    inlineLabel: 'terms of service',
    href: EXTERNAL_LINKS.termsOfService,
  },
  {
    key: 'privacy',
    label: 'Privacy Policy',
    inlineLabel: 'privacy policy',
    href: EXTERNAL_LINKS.privacyPolicy,
  },
  {
    key: 'aup',
    label: 'Acceptable Use Policy',
    inlineLabel: 'acceptable use policy',
    href: EXTERNAL_LINKS.acceptableUsePolicy,
  },
] as const satisfies ReadonlyArray<{
  key: string;
  label: string;
  inlineLabel: string;
  href: string;
}>;
